// Bottom sheet over the dimmed coop: the chore sheet, and anything else that
// needs the player to answer without leaving the yard.
//
// It is not a route. The world stays alive under it — the plan is a dialog, not
// a screen — so the coop screen is still mounted behind the backdrop.

import { button, h, icon } from '../dom';
import type { Shell } from '../shell';

const CLOSE_ICON = '<path d="M6 6l12 12"></path><path d="M18 6 6 18"></path>';

export interface SheetOptions {
  title: string;
  /** Closes the sheet. Defaults to the backdrop and Escape. */
  onClose: () => void;
}

export interface Sheet {
  /** The scrollable body to fill. */
  readonly body: HTMLElement;
  readonly root: HTMLElement;
  close: () => void;
}

export function openSheet(shell: Shell, options: SheetOptions): Sheet {
  const backdrop = h('div', { class: 'cf-sheet-backdrop' });
  const grip = h('div', { class: 'cf-sheet__grip', attrs: { 'aria-hidden': 'true' } });
  const heading = h('h1', { class: 'cf-title', text: options.title });
  const closeBtn = button(
    {
      class: 'cf-sheet__close',
      attrs: { 'aria-label': options.title === '' ? 'Close' : options.title },
      on: { click: () => close() },
    },
    icon({ shapes: CLOSE_ICON, size: 20, strokeWidth: 2.4 }),
  );
  const body = h('div', { class: 'cf-sheet__body cf-scroll' });
  const panel = h(
    'section',
    {
      class: 'cf-sheet',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': options.title },
    },
    grip,
    h('div', { class: 'cf-sheet__head' }, heading, closeBtn),
    body,
  );

  const root = h('div', { class: 'cf-sheet-layer' }, backdrop, panel);
  shell.sheets.appendChild(root);

  // Three ways out, because a phone player uses all three: the X, the backdrop,
  // and a swipe down on the panel itself.
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };
  const onBackdropClick = (): void => close();
  backdrop.addEventListener('click', onBackdropClick);
  document.addEventListener('keydown', onKey);

  const startDrag = (event: PointerEvent): void => {
    const startY = event.clientY;
    const onMove = (move: PointerEvent): void => {
      const delta = move.clientY - startY;
      if (delta > 0) panel.style.transform = `translateY(${delta}px)`;
    };
    const onUp = (up: PointerEvent): void => {
      panel.removeEventListener('pointermove', onMove);
      panel.removeEventListener('pointerup', onUp);
      const delta = up.clientY - startY;
      panel.style.transform = '';
      // A third of the panel's height, or a flick, closes it.
      if (delta > panel.clientHeight / 3) close();
    };
    panel.addEventListener('pointermove', onMove);
    panel.addEventListener('pointerup', onUp);
  };
  grip.addEventListener('pointerdown', startDrag);

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    root.remove();
    options.onClose();
  }

  // The grip is the drag handle, so it is a real target and says what it does.
  grip.setAttribute('role', 'button');
  grip.setAttribute('aria-label', 'Drag down to close');

  return { body, root, close };
}
